import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useUserStore } from '@/stores/user'
import router from '@/router'
import { WEB_CLIENT_TYPE, classifySessionFailure, getSessionFailureMessage, getWebDeviceId } from '@/utils/auth-session'

// ========== IM 专用 Axios 实例 ==========
// IM 后端响应码为 code: 0 或 code: 200 均视为成功

const baseURL = process.env.VUE_APP_API_BASE_URL || ''

const imRequest = axios.create({
  baseURL: baseURL + '/api/im',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
})

// 请求拦截器
imRequest.interceptors.request.use((config) => {
  const userStore = useUserStore()
  if (userStore.accessToken) {
    config.headers.Authorization = `${userStore.tokenType} ${userStore.accessToken}`
  }
  config.headers['X-Device-Id'] = getWebDeviceId()
  config.headers['X-Client-Type'] = WEB_CLIENT_TYPE
  return config
})

let sessionExpiredHandling = false

async function handleSessionInvalid(reason) {
  if (sessionExpiredHandling) {
    return
  }
  sessionExpiredHandling = true
  const message = getSessionFailureMessage(reason)
  try {
    const userStore = useUserStore()
    userStore.clearUser()
    await ElMessageBox.alert(message, reason === 'SESSION_REPLACED' ? '账号已在其他设备登录' : '登录状态已失效', {
      confirmButtonText: '重新登录',
      type: 'warning',
      closeOnClickModal: false,
      closeOnPressEscape: false,
      showClose: false
    })
    router.replace('/login')
  } finally {
    sessionExpiredHandling = false
  }
}

// 响应拦截器
imRequest.interceptors.response.use(
  async (response) => {
    if (response.config.responseType === 'blob') {
      return response.data
    }

    const { code, message, data } = response.data

    if (code === 401 || code === 1007 || code === 1011) {
      const reason = code === 1011 ? 'SESSION_REPLACED' : 'TOKEN_EXPIRED'
      await handleSessionInvalid(reason)
      return Promise.reject(new Error(getSessionFailureMessage(reason)))
    }

    if (code === 200 || code === 0) {
      return data
    }

    return Promise.reject(new Error(message || '请求失败'))
  },
  async (error) => {
    if (error.response) {
      const { status, data } = error.response
      if (status === 401) {
        await handleSessionInvalid(classifySessionFailure(error.response))
      } else {
        ElMessage.error(data?.message || '请求失败')
      }
    } else if (error.code === 'ECONNABORTED') {
      ElMessage.error('请求超时，请稍后重试')
    } else {
      ElMessage.error('网络连接失败')
    }
    return Promise.reject(error)
  }
)

// ========== 会话管理 API ==========

// 获取我的会话列表
export function getConversationList(params = {}) {
  return imRequest.get('/conversations', { params: { pageNum: 1, pageSize: 200, ...params } })
}

// 获取会话详情
export function getConversationDetail(conversationId) {
  return imRequest.get(`/conversations/${conversationId}`)
}

// 获取会话成员列表
export function getConversationMembers(conversationId) {
  return imRequest.get(`/conversations/${conversationId}/members`)
}

// 创建单聊会话
export function createSingleConversation(targetUserId, targetUserType) {
  const params = {}
  if (targetUserType !== undefined && targetUserType !== null) {
    params.targetUserType = targetUserType
  }
  return imRequest.post(`/conversations/single/${targetUserId}`, null, { params })
}

// 创建群聊会话
export function createGroupConversation(data) {
  return imRequest.post('/conversations/group', data)
}

// 添加群成员
export function addGroupMembers(conversationId, data) {
  return imRequest.post(`/conversations/${conversationId}/members`, data)
}

// 移除群成员
export function removeGroupMember(conversationId, data) {
  return imRequest.delete(`/conversations/${conversationId}/members`, { data })
}

// 退出群聊
export function leaveGroup(conversationId) {
  return imRequest.delete(`/conversations/${conversationId}/quit`)
}

// 更新会话个人设置（免打扰/置顶）
export function updateConversationSettings(conversationId, params = {}) {
  return imRequest.put(`/conversations/${conversationId}/settings`, null, { params })
}

// 更新群聊信息
export function updateGroupInfo(conversationId, data) {
  return imRequest.put(`/conversations/${conversationId}`, data)
}

// 解散群聊
export function dissolveGroup(conversationId) {
  return imRequest.delete(`/conversations/${conversationId}`)
}

// 标记会话已读
export function markConversationRead(conversationId) {
  return imRequest.put(`/conversations/${conversationId}/read`)
}

// 设置群成员角色
export function setMemberRole(conversationId, data) {
  return imRequest.put(`/conversations/${conversationId}/members/role`, data)
}

// 转让群主
export function transferOwner(conversationId, data) {
  return imRequest.put(`/conversations/${conversationId}/owner`, data)
}

// ========== 消息管理 API ==========

// 发送消息
export function sendMessage(data) {
  return imRequest.post('/messages/send', data)
}

// 撤回消息
export function revokeMessage(messageId) {
  return imRequest.put(`/messages/${messageId}/revoke`)
}

// 上传文件/图片消息
export function uploadFile(conversationId, file) {
  const formData = new FormData()
  formData.append('file', file)
  return imRequest.post(`/messages/file/${conversationId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000
  })
}
